import { TechnicalError, isRateLimitError } from '../../../common/errors';
import Result, { CancellableResult } from '../../../common/result';
import { Token } from '../../common/token';
import { InitializationError } from '../errors';
import {
  AuthenticationResult,
  FaceAuthentication,
  InitializationResult,
  SearchAuthenticationResult,
} from '../faceAuthentication';
import facetecConfigService from './configService';
import { FacetecResult } from './facetecProcessor';
import facetecService from './facetecService';
import resultMapper from './resultMapper';

export default class FacetecFaceAuthentication implements FaceAuthentication {
  private initialized = false;

  private token: Token | null = null;

  setToken(token: Token): void {
    this.token = token;
  }

  async initialize(): Promise<InitializationResult> {
    if (this.initialized) {
      return Result.noValueSuccess();
    }

    try {
      await facetecConfigService.fetchFacetecConfig();
      const result = await facetecService.initialize();
      this.initialized = result;
    } catch (error) {
      return Result.failed(new InitializationError('Unexpected error initializing facetec', error as Error));
    }

    return Result.noValueSuccess();
  }

  async authenticate(identityAuthId: string, withAutomaticEnrollment: boolean): Promise<AuthenticationResult> {
    // A token is required in order to authenticate
    if (!this.token) {
      return CancellableResult.failed(new TechnicalError('Missing token implementation'));
    }

    try {
      const initResult = await this.initialize();
      if (initResult.isFailed()) {
        return CancellableResult.failed(initResult.failure());
      }

      const options = { externalUserId: identityAuthId };
      const identifierResult = await facetecService.getEnrollmentIdentifier(identityAuthId, options);

      if (identifierResult.isFailed()) {
        // Rate limit errors should be returned directly
        return CancellableResult.failed(identifierResult.failure());
      }

      const identifier = identifierResult.success();

      let result: FacetecResult;

      if (withAutomaticEnrollment === false) {
        // authenticate only
        result = await facetecService.authenticate(identifier, this.token, options);
      } else {
        // authenticate if enrollment already exists, otherwise enroll
        const enrollmentExistsResult = await facetecService.enrollmentExists(identifier, this.token, options);
        if (enrollmentExistsResult.isFailed()) {
          // If it's a RateLimitError, rethrow it directly instead of wrapping
          const error = enrollmentExistsResult.failure();
          if (isRateLimitError(error)) {
            return CancellableResult.failed(error);
          }

          return CancellableResult.failed(
            new TechnicalError('Error determining if enrollment exists', enrollmentExistsResult.failure()),
          );
        }
        const enrollmentExists = enrollmentExistsResult.success();

        result = enrollmentExists
          ? await facetecService.authenticate(identifier, this.token, options)
          : await facetecService.enroll(identifier, this.token, options);
      }

      return resultMapper.mapAuthenticationResult(result);
    } catch (error) {
      return CancellableResult.failed(new TechnicalError('Unexpected authenticate error', error as Error));
    }
  }

  async faceIdAuthenticate(identityAuthId: string): Promise<AuthenticationResult> {
    // A token is required in order to authenticate
    if (!this.token) {
      return CancellableResult.failed(new TechnicalError('Missing token implementation'));
    }

    try {
      const initResult = await this.initialize();
      if (initResult.isFailed()) {
        return CancellableResult.failed(initResult.failure());
      }

      const options = { externalUserId: identityAuthId };
      const identifierResult = await facetecService.getEnrollmentIdentifier(identityAuthId, options);

      if (identifierResult.isFailed()) {
        // Rate limit errors should be returned directly
        return CancellableResult.failed(identifierResult.failure());
      }

      const identifier = identifierResult.success();

      // authenticate if enrollment already exists, otherwise enroll
      const enrollmentExistsResult = await facetecService.enrollmentExists(identifier, this.token, options);
      if (enrollmentExistsResult.isFailed()) {
        // If it's a RateLimitError, rethrow it directly instead of wrapping
        const error = enrollmentExistsResult.failure();
        if (isRateLimitError(error)) {
          return CancellableResult.failed(error);
        }

        return CancellableResult.failed(
          new TechnicalError('Error determining if enrollment exists', enrollmentExistsResult.failure()),
        );
      }
      const enrollmentExists = enrollmentExistsResult.success();

      const result = enrollmentExists
        ? await facetecService.faceIdAuthenticate(identifier, this.token, options)
        : await facetecService.faceIdEnroll(identifier, this.token, options);

      return resultMapper.mapAuthenticationResult(result);
    } catch (error) {
      return CancellableResult.failed(new TechnicalError('Unexpected faceIdAuthenticate error', error as Error));
    }
  }

  async search(): Promise<SearchAuthenticationResult> {
    try {
      const initResult = await this.initialize();
      if (initResult.isFailed()) {
        return CancellableResult.failed(initResult.failure());
      }

      const result = await facetecService.search();
      return resultMapper.mapSearchResult(result);
    } catch (error) {
      return CancellableResult.failed(new TechnicalError('Unexpected search face error', error as Error));
    }
  }
}
